import { useDashboardTab } from "./useDashboardTab";
import { getEvolution } from "@/services/dashboard/dashboard";
import { useEvolutionKpis } from "@/composables/admin/dashboard/useEvolutionKpis";
import { useEvolutionJornadas } from "@/composables/admin/dashboard/useEvolutionJornadas";
import { useEvolutionCharts } from "@/composables/admin/dashboard/useEvolutionCharts";

/**
 * Composable del panel de evolución del dashboard.
 * Orquesta sub-composables especializados:
 * - useEvolutionKpis: KPIs de variación (revenue/attendance change)
 * - useEvolutionJornadas: datos de charts, breakdown, brackets, racha
 * - useEvolutionCharts: opciones de Google Charts
 *
 * Delega el fetching, cancelación y retardo mínimo a useDashboardTab.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef — ref con los IDs de temporada seleccionados
 * @param {import('vue').Ref<Array>} seasonsRef — ref con la lista de temporadas
 * @returns {{
 *     evolutionData: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     kpis: import('vue').ComputedRef<{ revenue: object, attendance: object }|null>,
 *     chartData: import('vue').ComputedRef<Map<string, Array>>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     accumulatedChartData: import('vue').ComputedRef<Array>,
 *     revenueChange: import('vue').ComputedRef<number>,
 *     attendanceChange: import('vue').ComputedRef<number>,
 *     perClubData: import('vue').ComputedRef<{ jornadas: Array, selectedIndex: number }>,
 *     bracketAnalysis: import('vue').ComputedRef<Array|null>,
 *     rachaBadges: import('vue').ComputedRef<Array>,
 *     accumulatedChartOptions: import('vue').ComputedRef<object>,
 *     revenueAttendanceOptions: import('vue').ComputedRef<object>,
 *     positionRevenueOptions: import('vue').ComputedRef<object>,
 *     multiPositionOptions: import('vue').ComputedRef<object>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useEvolution(seasonIdsRef, seasonsRef) {
    const {
        data: evolutionData,
        loading,
        error,
        isMultiSeason,
        currentSeasonEntry,
        refresh,
    } = useDashboardTab(seasonIdsRef, seasonsRef, getEvolution);

    // KPIs de variación
    const { kpis, revenueChange, attendanceChange } = useEvolutionKpis(
        currentSeasonEntry,
        isMultiSeason,
    );

    // Datos derivados de jornadas (charts, breakdown, brackets, racha)
    const {
        accumulatedChartData,
        chartData,
        seasonBreakdown,
        perClubData,
        bracketAnalysis,
        rachaBadges,
    } = useEvolutionJornadas(
        evolutionData,
        isMultiSeason,
        currentSeasonEntry,
        seasonsRef,
    );

    // Opciones de Google Charts
    const {
        revenueAttendanceOptions,
        accumulatedChartOptions,
        positionRevenueOptions,
        multiPositionOptions,
    } = useEvolutionCharts(evolutionData, isMultiSeason);

    return {
        // Estado
        evolutionData,
        loading,
        error,

        // Computed
        kpis,
        chartData,
        isMultiSeason,
        seasonBreakdown,
        accumulatedChartData,
        accumulatedChartOptions,
        revenueAttendanceOptions,
        positionRevenueOptions,
        multiPositionOptions,
        revenueChange,
        attendanceChange,
        perClubData,
        bracketAnalysis,
        rachaBadges,

        // Métodos
        refresh,
    };
}
